// src/components/home/StoryUploader.jsx
import React, { useState, useRef, useEffect } from 'react';
import { db, storage } from '../../firebase';
import {
  collection, addDoc, serverTimestamp, Timestamp,
} from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import { useAuth } from '../../context/AuthContext';
import {
  validateMedia, uploadMedia, isVideoFile, MAX_STORY_PHOTOS,
} from '../../utils/feedHelpers';

/* ================================================================== */
/* Storage helpers                                                     */
/* ================================================================== */

const storagePathFromUrl = (url) => {
  if (!url) return null;
  const m = url.match(/\/o\/([^?]+)/);
  return m ? decodeURIComponent(m[1]) : null;
};

const safeDeleteFromStorage = async (url) => {
  const path = storagePathFromUrl(url);
  if (!path) return;
  try {
    await deleteObject(ref(storage, path));
  } catch (err) {
    if (err?.code !== 'storage/object-not-found') {
      console.warn('Storage cleanup skipped:', path, err?.code || err);
    }
  }
};

/* ================================================================== */
/* Media Tile                                                          */
/* ================================================================== */

const MediaTile = ({ item, onRemove, disabled }) => (
  <div className="relative group rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700">
    {item.type === 'video' ? (
      <video
        src={item.url}
        className="w-full h-24 object-cover"
        muted
        playsInline
        preload="metadata"
      />
    ) : (
      <img
        src={item.url}
        alt=""
        className="w-full h-24 object-cover"
        loading="lazy"
      />
    )}
    {item.type === 'video' && (
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-8 h-8 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center">
          <i className="fas fa-play text-white text-xs" aria-hidden="true" />
        </div>
      </div>
    )}
    <button
      type="button"
      onClick={onRemove}
      disabled={disabled}
      aria-label="Remove"
      className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-red-500 text-white flex items-center justify-center opacity-100 sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100 transition disabled:opacity-50 shadow-md z-10 active:scale-90"
    >
      <i className="fas fa-times text-xs" aria-hidden="true" />
    </button>
  </div>
);

/* ================================================================== */
/* Empty Picker                                                        */
/* ================================================================== */

const EmptyPicker = ({ onCamera, onFiles, disabled }) => (
  <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-6 text-center">
    <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-orange/10 flex items-center justify-center">
      <i className="fas fa-images text-orange text-2xl" aria-hidden="true" />
    </div>
    <p className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
      Add photos or videos
    </p>
    <div className="flex gap-2 justify-center">
      <button
        type="button"
        onClick={onCamera}
        disabled={disabled}
        className="flex items-center gap-2 bg-orange hover:bg-orange-dark text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 transition active:scale-95"
      >
        <i className="fas fa-camera" aria-hidden="true" />
        Camera
      </button>
      <button
        type="button"
        onClick={onFiles}
        disabled={disabled}
        className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 transition active:scale-95"
      >
        <i className="fas fa-folder-open" aria-hidden="true" />
        Gallery
      </button>
    </div>
  </div>
);

/* ================================================================== */
/* Story Uploader                                                      */
/* ================================================================== */

const StoryUploader = ({ onClose, authorName, authorPhoto }) => {
  const { user, isAdmin } = useAuth();

  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [caption, setCaption] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [error, setError] = useState('');

  const previewUrlsRef = useRef([]);
  const busyRef = useRef(false);
  const captionRef = useRef(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  useEffect(() => {
    return () => {
      previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      previewUrlsRef.current = [];
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && !busyRef.current) onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    if (files.length === 0) {
      const t = setTimeout(() => captionRef.current?.focus(), 150);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [files.length]);

  const totalFiles = files.length;
  const canAddMore = totalFiles < MAX_STORY_PHOTOS;
  const slotsLeft = MAX_STORY_PHOTOS - totalFiles;
  const slotsUsedPct = Math.min(100, (totalFiles / MAX_STORY_PHOTOS) * 100);

  const handleFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    if (!picked.length) return;

    const slots = MAX_STORY_PHOTOS - totalFiles;
    if (slots <= 0) return;

    const errors = [];
    const accepted = [];
    for (const f of picked) {
      if (accepted.length >= slots) break;
      const err = validateMedia(f);
      if (err) errors.push(err);
      else accepted.push(f);
    }
    setError(errors.length ? errors.join('\n') : '');
    if (!accepted.length) return;

    const nextFiles = [...files, ...accepted];

    previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    const nextPreviews = nextFiles.map((f) => ({
      url: URL.createObjectURL(f),
      type: isVideoFile(f) ? 'video' : 'image',
    }));
    previewUrlsRef.current = nextPreviews.map((p) => p.url);

    setFiles(nextFiles);
    setPreviews(nextPreviews);
  };

  const removeAt = (idx) => {
    const url = previews[idx]?.url;
    if (url) URL.revokeObjectURL(url);
    previewUrlsRef.current = previewUrlsRef.current.filter((u) => u !== url);

    setFiles(files.filter((_, i) => i !== idx));
    setPreviews(previews.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (busyRef.current) return;
    if (!files.length) {
      setError('Add at least one photo or video.');
      return;
    }

    busyRef.current = true;
    setUploading(true);
    setError('');
    setProgress({ done: 0, total: files.length });

    const uploadedUrls = [];

    try {
      const items = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const url = await uploadMedia(file, 'stories');
        uploadedUrls.push(url);
        items.push({
          url,
          type: isVideoFile(file) ? 'video' : 'image',
        });
        setProgress({ done: i + 1, total: files.length });
      }

      const now = new Date();
      const expires = new Date(now.getTime() + 24 * 60 * 60 * 1000);

      const approved = isAdmin === true;
      await addDoc(collection(db, 'stories'), {
        authorId: user.uid,
        authorName: authorName || 'User',
        authorPhoto: authorPhoto || null,
        items,
        photos: items.map((i) => i.url),
        caption: caption.trim(),
        createdAt: serverTimestamp(),
        expiresAt: Timestamp.fromDate(expires),
        views: [],
        viewCount: 0,
        approved,
        approvalStatus: approved ? 'approved' : 'pending',
        approvedBy: null,
        approvedAt: null,
      });

      onClose?.();
    } catch (err) {
      console.error('Story upload failed:', err);
      for (const url of uploadedUrls) {
        try {
          await safeDeleteFromStorage(url);
        } catch (cleanupErr) {
          console.warn('Cleanup failed for', url, cleanupErr);
        }
      }
      setError(err.message || 'Upload failed. Try again.');
    } finally {
      setUploading(false);
      busyRef.current = false;
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 overflow-y-auto"
      onClick={() => !uploading && onClose?.()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="story-uploader-title"
      style={{
        paddingTop: 'calc(0.75rem + env(safe-area-inset-top))',
        paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))',
      }}
    >
      <div
        className="bg-white dark:bg-gray-800 w-full rounded-2xl max-w-lg max-h-[95vh] sm:max-h-[90vh] flex flex-col overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex justify-between items-center px-4 sm:px-5 py-3 border-b dark:border-gray-700 flex-shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <i className="fas fa-plus-circle text-orange" aria-hidden="true" />
            <h3
              id="story-uploader-title"
              className="text-base sm:text-lg font-bold text-gray-800 dark:text-white truncate"
            >
              {isAdmin ? 'Create Story' : 'Create Story (Pending Review)'}
            </h3>
            {totalFiles > 0 && (
              <span className="text-[10px] text-gray-400 dark:text-gray-500 flex-shrink-0">
                {totalFiles}/{MAX_STORY_PHOTOS}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={uploading}
            aria-label="Close"
            className="w-9 h-9 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 disabled:opacity-50 flex items-center justify-center flex-shrink-0 transition active:scale-90"
          >
            <i className="fas fa-times" aria-hidden="true" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 sm:p-5 overflow-y-auto flex-1 space-y-3 min-h-0">
          {previews.length === 0 && (
            <>
              <EmptyPicker
                onCamera={() => cameraInputRef.current?.click()}
                onFiles={() => fileInputRef.current?.click()}
                disabled={uploading}
              />
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/mp4,video/webm,video/quicktime"
                multiple
                onChange={handleFiles}
                disabled={uploading}
                className="hidden"
              />
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFiles}
                disabled={uploading}
                className="hidden"
              />
            </>
          )}

          {previews.length > 0 && (
            <>
              <div className="flex items-center gap-2 text-[11px] text-gray-500 dark:text-gray-400">
                <div className="flex-1 h-1 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-orange transition-all duration-300"
                    style={{ width: `${slotsUsedPct}%` }}
                  />
                </div>
                <span className="font-medium">
                  {slotsLeft} slot{slotsLeft === 1 ? '' : 's'} left
                </span>
              </div>

              <div className="grid grid-cols-3 gap-2">
                {previews.map((p, i) => (
                  <MediaTile
                    key={`${p.url}-${i}`}
                    item={p}
                    onRemove={() => removeAt(i)}
                    disabled={uploading}
                  />
                ))}
              </div>

              {canAddMore && (
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => cameraInputRef.current?.click()}
                    disabled={uploading}
                    className="flex-1 flex items-center justify-center gap-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 py-2 rounded-lg text-xs font-medium disabled:opacity-50 transition active:scale-95"
                  >
                    <i className="fas fa-camera" aria-hidden="true" />
                    Camera
                  </button>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="flex-1 flex items-center justify-center gap-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 py-2 rounded-lg text-xs font-medium disabled:opacity-50 transition active:scale-95"
                  >
                    <i className="fas fa-plus" aria-hidden="true" />
                    Add more
                  </button>
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/mp4,video/webm,video/quicktime"
                multiple
                onChange={handleFiles}
                disabled={uploading}
                className="hidden"
              />
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFiles}
                disabled={uploading}
                className="hidden"
              />
            </>
          )}

          <div>
            <label
              htmlFor="story-caption"
              className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300"
            >
              Caption (optional)
            </label>
            <textarea
              id="story-caption"
              ref={captionRef}
              rows={2}
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              disabled={uploading}
              maxLength={200}
              placeholder="Say something about this story…"
              className="w-full p-2 border rounded-lg text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange resize-none"
            />
            <p className="text-[10px] text-gray-400 text-right mt-0.5">
              {caption.length}/200
            </p>
          </div>

          {error && (
            <div
              role="alert"
              className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-200 text-xs rounded-md p-2 whitespace-pre-line"
            >
              {error}
            </div>
          )}

          {uploading && progress.total > 0 && (
            <div className="text-sm text-gray-600 dark:text-gray-300">
              <div
                className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden"
                role="progressbar"
                aria-valuenow={progress.done}
                aria-valuemin={0}
                aria-valuemax={progress.total}
              >
                <div
                  className="h-full bg-orange transition-all duration-300"
                  style={{ width: `${(progress.done / progress.total) * 100}%` }}
                />
              </div>
              <p className="text-xs mt-1 text-center">
                Uploading {progress.done} of {progress.total}…
              </p>
            </div>
          )}

          <p className="text-[10px] text-gray-500 dark:text-gray-400 text-center flex items-center justify-center gap-1">
            <i className="fas fa-clock" aria-hidden="true" />
            Your story will be visible for 24 hours.
          </p>

          {!isAdmin && (
            <p className="text-[10px] text-gray-500 dark:text-gray-400 text-center flex items-center justify-center gap-1">
              <i className="fas fa-info-circle" aria-hidden="true" />
              Your story will be visible after admin approval.
            </p>
          )}
        </div>

        {/* Footer */}
        <div
          className="px-4 sm:px-5 py-3 border-t dark:border-gray-700 flex justify-end gap-2 flex-shrink-0 bg-white dark:bg-gray-800"
          style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={uploading}
            className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg text-sm disabled:opacity-50 transition active:scale-95"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={uploading || !files.length}
            className="px-4 py-2 bg-orange hover:bg-orange-dark text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-2 transition active:scale-95"
          >
            {uploading ? (
              <>
                <i className="fas fa-spinner fa-spin" aria-hidden="true" />
                Uploading…
              </>
            ) : isAdmin ? (
              <>
                <i className="fas fa-paper-plane" aria-hidden="true" />
                Publish
              </>
            ) : (
              <>
                <i className="fas fa-paper-plane" aria-hidden="true" />
                Submit for Review
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StoryUploader;